<template>
  <div class="settings-tab">
    <!-- Sync Configuration -->
    <div class="settings-section" v-if="isConnected">
      <h3>Sync Configuration</h3>
      <ConfigPanel />
    </div>

    <!-- User Preferences -->
    <div class="settings-section">
      <h3>User Preferences</h3>
      <div class="preferences-form">
        <div class="preference-item">
          <label>
            <input 
              type="checkbox" 
              v-model="preferences.emailNotifications"
              @change="savePreferences"
            />
            Email Notifications
          </label>
          <p class="preference-description">
            Receive email notifications for sync errors and important events
          </p>
        </div>

        <div class="preference-item">
          <label>
            <input 
              type="checkbox" 
              v-model="preferences.autoRefresh"
              @change="savePreferences"
            />
            Auto-refresh Dashboard
          </label>
          <p class="preference-description">
            Automatically refresh dashboard data every 30 seconds
          </p>
        </div>

        <div class="preference-item">
          <label>
            Items per page:
            <select v-model="preferences.itemsPerPage" @change="savePreferences" class="select-field">
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </label>
          <p class="preference-description">
            Number of items to display per page in tables
          </p>
        </div>
      </div>
    </div>

    <!-- App Settings -->
    <div class="settings-section">
      <h3>App Settings</h3>
      <div class="app-settings">
        <div class="setting-item">
          <div class="setting-info">
            <strong>App Version</strong>
            <p>Current version of the integration app</p>
          </div>
          <div class="setting-value">1.0.0</div>
        </div>

        <div class="setting-item">
          <div class="setting-info">
            <strong>Location ID</strong>
            <p>Your {{ brand.agencyName }} location identifier</p>
          </div>
          <div class="setting-value">{{ locationId || 'N/A' }}</div>
        </div>

        <div class="setting-item">
          <div class="setting-info">
            <strong>Company ID</strong>
            <p>Your {{ brand.agencyName }} company identifier</p>
          </div>
          <div class="setting-value">{{ companyId || 'N/A' }}</div>
        </div>

        <div class="setting-item">
          <div class="setting-info">
            <strong>Last Updated</strong>
            <p>When the app was last updated</p>
          </div>
          <div class="setting-value">{{ lastUpdated }}</div>
        </div>

        <div class="setting-item">
          <div class="setting-info">
            <strong>{{ brand.agencyName }} Webhook URL</strong>
            <p>Configure this URL in your {{ brand.agencyName }} automation webhooks</p>
          </div>
          <div class="setting-value webhook-url">{{ webhookUrl }}</div>
        </div>

        <div class="setting-item">
          <div class="setting-info">
            <strong>ServiceM8 Webhook URL</strong>
            <p>Configure this URL in your ServiceM8 webhook subscriptions</p>
          </div>
          <div class="setting-value webhook-url">{{ servicem8WebhookUrl }}</div>
        </div>
      </div>
    </div>

    <!-- Danger Zone -->
    <div class="settings-section danger-zone">
      <h3>Danger Zone</h3>
      <div class="danger-actions">
        <button @click="disconnectServiceM8" class="btn-danger" :disabled="!isConnected">
          Disconnect ServiceM8
        </button>
        <button @click="clearAllMappings" class="btn-danger">
          Clear All Mappings
        </button>
        <button @click="resetAllSettings" class="btn-danger">
          Reset All Settings
        </button>
      </div>
      <p class="danger-warning">
        Warning: These actions cannot be undone. Please proceed with caution.
      </p>
    </div>
  </div>
</template>

<script>
import ConfigPanel from './ConfigPanel.vue'
import { brandMixin } from '../brandMixin'

export default {
  name: 'SettingsTab',
  mixins: [brandMixin],
  components: {
    ConfigPanel,
  },
  props: {
    locationId: {
      type: String,
      default: '',
    },
    companyId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isConnected: false,
      preferences: {
        emailNotifications: false,
        autoRefresh: false,
        itemsPerPage: 25,
      },
      lastUpdated: new Date().toLocaleDateString(),
    }
  },
  computed: {
    webhookUrl() {
      return `${window.location.origin}/webhooks/ghl`;
    },
    servicem8WebhookUrl() {
      return `${window.location.origin}/webhooks/servicem8`;
    }
  },
  async mounted() {
    await this.checkConnectionStatus();
    await this.loadPreferences();
  },
  methods: {
    async checkConnectionStatus() {
      try {
        const ssoToken = await this.getSSOToken();
        const response = await fetch('/servicem8/status', {
          method: 'GET',
          headers: {
            'x-sso-token': ssoToken,
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          this.isConnected = data.connected;
        }
      } catch (error) {
        console.error('Error checking connection status:', error);
      }
    },
    async loadPreferences() {
      // Load preferences from localStorage or API
      const saved = localStorage.getItem('userPreferences');
      if (saved) {
        try {
          this.preferences = JSON.parse(saved);
        } catch (e) {
          console.error('Error loading preferences:', e);
        }
      }
    },
    async savePreferences() {
      // Save preferences to localStorage or API
      localStorage.setItem('userPreferences', JSON.stringify(this.preferences));
      // TODO: Save to backend API when endpoint is available
    },
    async disconnectServiceM8() {
      if (!confirm('Are you sure you want to disconnect ServiceM8? This will stop all syncing.')) {
        return;
      }

      try {
        const ssoToken = await this.getSSOToken();
        const response = await fetch('/servicem8/disconnect', {
          method: 'POST',
          headers: {
            'x-sso-token': ssoToken,
          },
        });

        if (response.ok) {
          this.isConnected = false;
          alert('ServiceM8 disconnected successfully');
        } else {
          alert('Failed to disconnect ServiceM8');
        }
      } catch (error) {
        console.error('Error disconnecting ServiceM8:', error);
        alert('Error disconnecting ServiceM8');
      }
    },
    async clearAllMappings() {
      if (!confirm('Are you sure you want to clear all contact and job mappings? This cannot be undone.')) {
        return;
      }

      if (!confirm('This will delete ALL mappings. Are you absolutely sure?')) {
        return;
      }

      try {
        // const ssoToken = await this.getSSOToken();
        // TODO: Implement clear mappings endpoint
        alert('Clear mappings functionality will be implemented soon');
      } catch (error) {
        console.error('Error clearing mappings:', error);
        alert('Error clearing mappings');
      }
    },
    async resetAllSettings() {
      if (!confirm('Are you sure you want to reset all settings to defaults? This cannot be undone.')) {
        return;
      }

      try {
        // Reset preferences
        this.preferences = {
          emailNotifications: false,
          autoRefresh: false,
          itemsPerPage: 25,
        };
        await this.savePreferences();
        
        // Reset sync config
        const ssoToken = await this.getSSOToken();
        const response = await fetch('/servicem8/config', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-sso-token': ssoToken,
          },
          body: JSON.stringify({
            autoSyncEnabled: false,
            leadToJobEnabled: false,
            jobSyncDirection: 'both',
            contactSyncEnabled: false,
            syncDirection: 'both',
          }),
        });

        if (response.ok) {
          alert('All settings reset to defaults');
          location.reload();
        } else {
          alert('Failed to reset settings');
        }
      } catch (error) {
        console.error('Error resetting settings:', error);
        alert('Error resetting settings');
      }
    },
    async getSSOToken() {
      return await new Promise((resolve) => {
        window.parent.postMessage({ message: "REQUEST_USER_DATA" }, "*");
        window.addEventListener("message", ({ data }) => {
          if (data.message === "REQUEST_USER_DATA_RESPONSE") {
            resolve(data.payload);
          }
        });
      });
    },
  },
}
</script>

<style scoped>
.settings-tab {
  padding: 20px;
}

.settings-section {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
}

.settings-section h3 {
  margin-top: 0;
  margin-bottom: 20px;
  color: #2c3e50;
  border-bottom: 2px solid #24ACFE;
  padding-bottom: 10px;
}

.preferences-form {
  margin-top: 15px;
}

.preference-item {
  margin-bottom: 20px;
  padding: 15px;
  background: #f5f5f5;
  border-radius: 6px;
}

.preference-item label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 500;
  margin-bottom: 8px;
}

.preference-description {
  margin: 8px 0 0 0;
  font-size: 13px;
  color: #666;
  font-style: italic;
}

.select-field {
  margin-left: 10px;
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.app-settings {
  margin-top: 15px;
}

.setting-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  margin-bottom: 10px;
  background: #f5f5f5;
  border-radius: 6px;
}

.setting-info {
  flex: 1;
}

.setting-info strong {
  display: block;
  margin-bottom: 5px;
  color: #2c3e50;
}

.setting-info p {
  margin: 0;
  font-size: 13px;
  color: #666;
}

.setting-value {
  font-weight: 500;
  color: #24ACFE;
}

.webhook-url {
  font-family: monospace;
  background: #eee;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  word-break: break-all;
}

.danger-zone {
  border: 2px solid #f44336;
}

.danger-zone h3 {
  color: #f44336;
  border-bottom-color: #f44336;
}

.danger-actions {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  margin-top: 15px;
}

.btn-danger {
  padding: 12px 24px;
  background: #f44336;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 500;
  transition: background 0.3s ease;
}

.btn-danger:hover:not(:disabled) {
  background: #d32f2f;
}

.btn-danger:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.danger-warning {
  margin-top: 15px;
  padding: 10px;
  background: #ffebee;
  color: #c62828;
  border-radius: 4px;
  font-size: 14px;
  font-style: italic;
}

.status-loading {
  padding: 15px;
  text-align: center;
  color: #666;
  font-style: italic;
}

.status-connected,
.status-disconnected {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 15px;
  background: #f5f5f5;
  border-radius: 6px;
}

.status-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
}

.status-indicator.connected {
  background-color: #4caf50;
  box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.2);
}

.status-indicator.disconnected {
  background-color: #f44336;
  box-shadow: 0 0 0 3px rgba(244, 67, 54, 0.2);
}

.ghl-details {
  margin-top: 15px;
  padding: 15px;
  background: white;
  border-radius: 6px;
  border-left: 3px solid #24ACFE;
}

.ghl-details p {
  margin: 8px 0;
  font-size: 14px;
  color: #2c3e50;
}

.ghl-details strong {
  color: #24ACFE;
  margin-right: 8px;
}

.status-message {
  margin-top: 10px;
  font-size: 13px;
  color: #666;
  font-style: italic;
}
</style>

